/* eslint-disable no-unused-expressions */
import {
	isNonEmptyObject,
	isNonEmptyString,
	isNonEmptyArray,
	isNumber,
	isBool,
} from "@cayuse/shared-react.utils.type-checks";
import _, { has } from "lodash";
import moment from "moment";

import {
	COLUMN_HEADER_KEYS,
	FIELD_TYPES,
	FLOWABLE_BASE_URL,
	FORM_OUTCOME_COMPLETE,
	RT_OBJECT_DEFAULT_VALUE_KEY,
	IS_DEV,
	CAYUSE_TYPES,
} from "globals/constants";
import { formSelectors } from "store/selectors";
import {
	dateWriteFormatter,
	formatDatesInFormPayload,
	getClassificationCodesMissingCount,
	notifyError,
	getRTObjectMissingCount,
	isFormFieldRequired,
	getFundingProgramObjectMissingCount,
	fileUploadCompleted,
	isRichTextEmpty,
	getBudgetBuilderObjectMissingCount,
	getDataTableMissingCount,
	isFormFieldEnabled,
} from "utils";
import { downloadFile, downloadMultipartFile } from "utils/proxy";

import styles from "./styles.css";

const EXPIRATION_TIME_MINUTES = 10;
const PLATFORM_FILE_PREFIX = "platform://";

const prepareFormTabs = section => {
	const sectionTitle = section.label;
	const sectionLayout = section?.extraSettings?.layoutDefinition?.rows || [];

	// Adding custom class to each form section/tab
	section.styleClass = `${styles.formSection} formSection`;

	// Adding title to each form section/tab
	sectionLayout.unshift({
		cols: [
			{
				styleClass: styles.formSectionTitle,
				type: "htmlComponent",
				value: `<h3 class="${styles.sectionTitle}">${sectionTitle}</h3>`,
			},
		],
	});
};

const findTabComponents = data => {
	if (data.rows?.length > 0) {
		const tabs = data.rows.find(row =>
            row.cols?.find(column => column.type === "tabs")
		)?.cols[0];
		if (!tabs) {
			return findTabComponents(
                data.rows[0].cols[0].extraSettings?.layoutDefinition
			);
		} else {
			const sections = tabs.extraSettings?.sections || [];
			sections.forEach(section => prepareFormTabs(section));

			return tabs;
		}
	}
};

const updateFormData = formConfig => {
	if (formConfig.outcomes == null || formConfig.outcomes.length === 0) {
		formConfig.outcomes = [
			{ value: FORM_OUTCOME_COMPLETE, label: "Complete" },
		];
	}

	const tabsConfig = findTabComponents(formConfig);

	const subFormWrapper = formConfig?.rows[0]?.cols[0]; // Forcing tabs to be the main component on the form

	if (subFormWrapper && subFormWrapper.type === "subform") {
		const subFormLayout = subFormWrapper?.extraSettings?.layoutDefinition;

		if (isNonEmptyObject(tabsConfig) && isNonEmptyObject(subFormLayout)) {
			subFormLayout.rows = [ { cols: [ tabsConfig ] } ];
		}
	}

	return tabsConfig;
};

const getFormDefinitionId = formattedPayload => {
	const caseInstanceId = formattedPayload?.self?.id;
	const parentCaseId = formattedPayload.parent?.id;
	const modificationFormDefinitionId =
        formattedPayload.parent?.modificationFormDefinitionId;

	return modificationFormDefinitionId != null &&
        parentCaseId !== caseInstanceId
		? modificationFormDefinitionId
		: formattedPayload.formDefinitionId ??
              formattedPayload.root?.formDefinitionId;
};

/**
 * Removes field key if value is null || "" || undefined || invalid date.
 * This function mutates passed argument.
 */
const normalizePayload = (payload, formElements, formType) => {
	if (!formElements) {
		return;
	}

	const formContent =
        {
        	[COLUMN_HEADER_KEYS.AWARD]: payload.root?.awardContent || {},
        	[COLUMN_HEADER_KEYS.PROPOSAL]: payload.root?.proposalContent || {},
        	[COLUMN_HEADER_KEYS.SUBAWARD]: payload.root?.subawardContent || {},
        }[formType] || {};

	// Award modifications makes award different than other forms.
	const contentKeyPrefix =
        {
        	[COLUMN_HEADER_KEYS.AWARD]: "root.awardContent",
        	[COLUMN_HEADER_KEYS.PROPOSAL]: "root.proposalContent",
        	[COLUMN_HEADER_KEYS.SUBAWARD]: "root.subawardContent",
        }[formType] || "";

	Object.keys(formContent).forEach(fieldContentKey => {
		const valueKey = `${contentKeyPrefix}.${fieldContentKey}`;
		const fieldType = formElements?.find(
			({ value }) => value === fieldContentKey || value === valueKey
		)?.type;
		const answer =
            formContent[
            	fieldContentKey
            ]; /* can be an object, array, boolean, string, undefined */
		const isAnswerEmpty =
            answer === null ||
            answer === "" ||
            answer === undefined; /* allows 0 as valid input */

		if (isAnswerEmpty) {
			delete formContent[fieldContentKey];
		} else if (
			[ FIELD_TYPES.DATE, FIELD_TYPES.DATETIME ].includes(fieldType)
		) {
			const isDateValid = moment(answer).isValid();

			if (!isDateValid) {
				delete formContent[fieldContentKey];
			}
		}
	});
};

//TODO Add dates comparison
const isSamePayload = (oldPayload, newPayload) =>
	_.isEqual(oldPayload, newPayload);

/*
 * This function mutates passed argument.
 * Remove attachment objects that have `status:1` (deleted) from the payload to prevent finding conflicts due to these files being considered deleted from the form.
 */
const removeAttachmentObjectsWithStatusDeleted = (
	payload,
	payloadRootContentKey
) => {
	const filterAttachmentsByStatus = array => {
		return array.reduce((acc, item) => {
			const isAttachment = has(item, "metadataProperties");
			if (isAttachment) {
				if (
					item.status !== 1 &&
                    (has(item, "id") || has(item, "name"))
				) {
					acc.push(item);
				}
			} else {
				acc.push(item);
			}

			return acc;
		}, []);
	};

	if (payload.root) {
		payload.root[payloadRootContentKey] = Object.fromEntries(
			Object.entries(payload.root[payloadRootContentKey] ?? {}).map(
				([ _, v ]) => [
					_,
					isNonEmptyArray(v) ? filterAttachmentsByStatus(v) : v,
				]
			)
		);
	}

	return payload;
};

const removeEmptyFields = payloadContent => {
	return Object.fromEntries(
		Object.entries(payloadContent ?? {})
			.filter(
				([ _, v ]) =>
					isNonEmptyObject(v) ||
                    isNonEmptyString(v) ||
                    isNonEmptyArray(v) ||
                    isNumber(v) ||
                    moment(v).isValid() ||
                    isBool(v)
			)
			.map(([ _, v ]) => [ _, isNonEmptyArray(v) ? v.filter(Boolean) : v ])
	);
};

const removeInconsistentAttachmentObjects = payload => {
	const payloadRootContentKey = formSelectors.entity().payloadRootContentKey;

	if (
		!payloadRootContentKey ||
        !payload.root ||
        _.isEmpty(payload.root[payloadRootContentKey])
	) {
		return payload;
	}

	payload.root[payloadRootContentKey] = Object.fromEntries(
		Object.entries(payload.root[payloadRootContentKey]).map(([ _, v ]) => {
			if (isNonEmptyArray(v)) {
				return [
					_,
					v.filter(object => {
						if (object.hasOwnProperty("metadataProperties")) {
							return (
								object.hasOwnProperty("id") ||
                                object.hasOwnProperty("name")
							);
						} else {
							return true;
						}
					}),
				];
			}

			return [ _, v ];
		})
	);

	return payload;
};

const formatPayload = payload => {
	let formattedPayload = formatDatesInFormPayload(
		payload,
		dateWriteFormatter
	);

	// CRUX-6265: This if-else sentence is causing problems in the single sor branch
	/*if (!payload?.containerFormKey) {
        formattedPayload = _.omit(formattedPayload, [
            "root",
            "proposalContent",
            "parent",
        ]);
    } else {
        formattedPayload = _.omit(formattedPayload, [
            "awardContent",
            "budgetBuilderContent",
            "proposalContent",
            "parent",
            "subawardContent",
        ]);
    }*/

	const payloadRootContentKey = formSelectors.entity().payloadRootContentKey;

	if (payload?.createAwardModificationTaskId) {
		formattedPayload[payloadRootContentKey] = removeEmptyFields(
			formattedPayload[payloadRootContentKey]
		);
	} else {
		formattedPayload.root[payloadRootContentKey] = removeEmptyFields(
			formattedPayload.root[payloadRootContentKey]
		);
	}

	formattedPayload = removeAttachmentObjectsWithStatusDeleted(
		formattedPayload,
		payloadRootContentKey
	);

	return formattedPayload;
};

const findActiveTab = (sections, uploadTypes, justActivatedSectionLabel) => {
	let activeTab = null;
	sections.forEach(section => {
		const sectionId = section.id;
		section.extraSettings.layoutDefinition.rows.forEach(row => {
			row.cols.forEach(col => {
				if (
					uploadTypes.includes(col.type) &&
                    col.label === justActivatedSectionLabel
				) {
					activeTab = sectionId;
				}
			});
		});
	});

	return activeTab;
};

const getRequiredFieldsCount = async (extraSettings, formContent) => {
	let requiredFieldsMissing = 0;
	const sectionLayout = extraSettings?.layoutDefinition?.rows || [];

	for (const row of sectionLayout) {
		const columns = row?.cols || [];

		for (const column of columns) {
			if (!column.type || column.type === "htmlComponent") {
				// skip below code if column is section title or type is empty
				continue;
			}

			const valueKey = column.value && column.value.replace(/[{}]/g, "");
			const { visible, isRequired, enabled, cayuseType, type, ignore } =
                column;

			if (valueKey) {
				if (
					cayuseType === FIELD_TYPES.DATA_TABLE ||
                    type === FIELD_TYPES.DATA_TABLE
				) {
					const isFiesldEnabled = isFormFieldEnabled({
						visible,
						enabled,
						formContent,
						ignore,
					});
					if (!isFiesldEnabled) {
						continue;
					}
					requiredFieldsMissing += getDataTableMissingCount(
						{
							formContent,
							key: valueKey,
							...column.extraSettings,
						}
					);
				}

				const isFieldRequired = isFormFieldRequired({
					valueKey,
					isRequired,
					visible,
					enabled,
					formContent,
					type,
					ignore,
				});

				if (!isFieldRequired) {
					continue;
				}

				if (
					cayuseType === CAYUSE_TYPES.RESEARCH_TEAM ||
                    type === FIELD_TYPES.RESEARCH_TEAM
				) {
					const RTObjectMissingCount = await getRTObjectMissingCount({
						RTObjectValueKey:
                            valueKey || RT_OBJECT_DEFAULT_VALUE_KEY,
						formContent,
						extraSettings: column.extraSettings,
					});

					requiredFieldsMissing += RTObjectMissingCount;
				} else if (
					cayuseType === CAYUSE_TYPES.CLASSIFICATION_CODES ||
                    type === FIELD_TYPES.CLASSIFICATION_CODES
				) {
					requiredFieldsMissing += getClassificationCodesMissingCount(
						{
							isRequired,
							formContent,
							key: valueKey,
							...column.extraSettings,
						}
					);
				} else if (
					cayuseType === CAYUSE_TYPES.FUNDING_PROGRAM ||
                    type === FIELD_TYPES.FUNDING_PROGRAM
				) {
					const missingCount = getFundingProgramObjectMissingCount({
						value: formContent[valueKey],
					});

					requiredFieldsMissing += missingCount;
				} else if (type === FIELD_TYPES.UPLOAD) {
					const allFilesUploaded = fileUploadCompleted(formContent[valueKey]);
					if(!allFilesUploaded) {
						requiredFieldsMissing++;
					}
				} else if (type === FIELD_TYPES.RICH_TEXT) {
					if (isRichTextEmpty(formContent[valueKey])) {
						requiredFieldsMissing++;
					}
				} else if (type === FIELD_TYPES.ONE) {
					if (formContent[valueKey] === undefined || formContent[valueKey] === null) {
						requiredFieldsMissing++;
					}
				} else if (cayuseType === CAYUSE_TYPES.CAYUSE_SUMMARY_BUDGET_IMPORTER || type === FIELD_TYPES.BUDGET_BUILDER) {
					const budgetBuilderObjectMissingCount = getBudgetBuilderObjectMissingCount(formContent[valueKey]);
					requiredFieldsMissing += budgetBuilderObjectMissingCount;
				} else {
					requiredFieldsMissing++;
				}
			}
		}
	}

	return requiredFieldsMissing;
};

const getReviewedSectionsParam = (formData, formType, tabName) => {
	switch (formType) {
	case COLUMN_HEADER_KEYS.AWARD:
		return { id: formData.awardId, tab: tabName, type: formType };
	case COLUMN_HEADER_KEYS.PROPOSAL:
		return { id: formData.proposalId, tab: tabName, type: formType };
	case COLUMN_HEADER_KEYS.SUBAWARD:
		return {
			id: formData.awardId,
			subawardId: formData.subawardId,
			tab: tabName,
			type: formType,
		};
	}
};

const getActiveTab = (
	extraSettings,
	uploadTypes,
	justActivatedSectionLabel
) => {
	let activeTab = extraSettings.activeTab;

	if (
		isNonEmptyString(justActivatedSectionLabel) &&
        isNonEmptyObject(extraSettings)
	) {
		activeTab = findActiveTab(
			extraSettings.sections,
			uploadTypes,
			justActivatedSectionLabel
		);

		return activeTab;
	}

	return activeTab;
};

const handleFileDownload = ({ href, title, shouldClose }, caseInstanceId) => {
	if (isNonEmptyString(href) && !shouldClose) {
		const rerouteUrl = () => {
			const hasProposalPath = href.includes("proposals/");
			const hasAwardPath = href.includes("awards/");
			const hasSubawardPath = href.includes("subawards/");

			if (hasProposalPath || hasAwardPath || hasSubawardPath) {
				const contentServiceMatch = href.match(/\/content-service\/[^?#]+/);
				if (contentServiceMatch) {
					const apiPath = contentServiceMatch[0];

					return `${FLOWABLE_BASE_URL}/cayuse-api${apiPath}`;
				}
			}

			return href;
		};

		downloadFile(rerouteUrl(), title).catch(error => {
			const message = IS_DEV
				? error.message
				: "Unable to download the attachment. Please try downloading it again.";
			notifyError(message, error);
		});
	}
};

const handlePlatformFileDownload = (href, fileName, shouldClose) => {
	if (isNonEmptyString(href) && !shouldClose) {
		downloadMultipartFile({
			id: href.split(PLATFORM_FILE_PREFIX).pop(),
			fileName: fileName,
		});
	}
};

const handleClick = (event, caseInstanceId) => {
	const filecardSelector = "div.flw__fileCard";
	const filecardAnchorSelector = "a.flw__fileCard__fileName--downloadable";
	const filecardElement = event.target.closest(filecardSelector);

	const isCloseButton = event => {
		const { nodeName, children } = event.target;

		const isAnchor = nodeName.toUpperCase() === "A";
		const isSvg = nodeName.toUpperCase() === "SVG";
		const isPath = nodeName.toUpperCase() === "PATH";
		const isCloseAnchor = isAnchor && children.length > 0;

		return isSvg || isPath || isCloseAnchor;
	};

	if (filecardElement) {
		const anchor = filecardElement.querySelector(filecardAnchorSelector);
		const shouldClose = isCloseButton(event);
		event.preventDefault();

		if (isNonEmptyString(anchor?.href)) {
			if (anchor.href.startsWith(PLATFORM_FILE_PREFIX)) {
				handlePlatformFileDownload(anchor.href, anchor.text, shouldClose);
			} else {
				handleFileDownload(
					{ href: anchor.href, title: anchor.text, shouldClose },
					caseInstanceId
				);
			}
		}
	}
};

const sectionPreviouslyReviewedByUser = (
	currentTab,
	sectionReviewedByList,
	userAccountId
) => {
	if (isNonEmptyArray(sectionReviewedByList)) {
		const reviewedBy = sectionReviewedByList.find(s =>
			s.startsWith(currentTab + ":")
		);
		if (!reviewedBy) {
			return false;
		}

		return reviewedBy.split(":")[1].split(",").includes(userAccountId);
	} else {
		return false;
	}
};

const getSectionStatus = async (
	id,
	extraSettings,
	currentTab,
	index,
	formStatus,
	payload,
	sectionReviewedByList,
	userAccountId,
	payloadRootContentKey
) => {
	const isSelectedSection = id === currentTab;

	(extraSettings.layoutDefinition.rows ?? []).forEach(row => {
		(row.cols ?? []).forEach(col => {
			if (col.type === "upload") {
				if (!col.extraSettings.uploadUrl) {
					col.extraSettings = {
						...col.extraSettings,
						uploadUrl: `${FLOWABLE_BASE_URL}/platform-api/content-items/provisional`,
						updateFileUrl: `${FLOWABLE_BASE_URL}/platform-api/content-items/new-version`,
					};
				}

				if (!col.extraSettings.autoupload) {
					col.extraSettings = {
						...col.extraSettings,
						autoupload: true,
					};
				}
			}
		});
	});

	const currentSectionStatus =
        isNonEmptyArray(formStatus) && formStatus[index]
        	? formStatus[index]
        	: {};

	const isSectionSeen =
        isSelectedSection ||
        currentSectionStatus.seen ||
        sectionPreviouslyReviewedByUser(
        	id,
        	sectionReviewedByList,
        	userAccountId
        );

	let isSectionInvalid = currentSectionStatus.sectionType === "invalid";

	const payloadContent = payload?.createAwardModificationTaskId
		? payload[payloadRootContentKey]
		: payload?.root[payloadRootContentKey];
	const content = Object.fromEntries(
		Object.entries(payloadContent ?? {}).filter(([ _, v ]) => v != null)
	);

	const requiredFieldsMissing = await getRequiredFieldsCount(
		extraSettings,
		content
	);

	if (requiredFieldsMissing > 0) {
		return {
			id,
			requiredFieldsMissing,
			sectionType: "missingFields",
			seen: isSectionSeen,
		};
	} else if (!isSectionSeen) {
		return {
			id,
			requiredFieldsMissing,
			sectionType: "unseen",
			seen: isSectionSeen,
		};
	} else if (isSectionInvalid) {
		return {
			id,
			requiredFieldsMissing,
			sectionType: "invalid",
			seen: isSectionSeen,
		};
	} else {
		return {
			id,
			requiredFieldsMissing,
			sectionType: "complete",
			seen: isSectionSeen,
		};
	}
};

const processEditLock = (editLock, userAccountId) => {
	const isNotExpired =
        moment().diff(
        	editLock.lockUpdatedTime ?? editLock.lockStartedTime,
        	"minutes"
        ) <= EXPIRATION_TIME_MINUTES;
	const isSameUser = editLock.userId === userAccountId;

	return { isNotExpired, isSameUser };
};

const hasSection = (sections, tabName) =>
	sections.find(section => section?.split(":")[0] === tabName);

const removeSection = (sections, tabName) =>
	sections.filter(section => section?.split(":")[0] !== tabName);

const containsField = (fields, field) => {
	return fields.find(item => item.id === field.id);
};

const Helper = {
	updateFormData,
	getFormDefinitionId,
	normalizePayload,
	formatPayload,
	isSamePayload,
	getReviewedSectionsParam,
	sectionPreviouslyReviewedByUser,
	getActiveTab,
	handleClick,
	hasSection,
	removeSection,
	getSectionStatus,
	removeInconsistentAttachmentObjects,
	processEditLock,
	containsField,
	formRef: {},
};

export default Helper;
